/**
 * Adapter / transformation layer for the Careers job listings (PRD §8).
 *
 * The Careers UI consumes our internal API ({ jobs: [...] }, served by the
 * Netlify Function / setupProxy, which owns the Keka Hire integration — see
 * jobsApi). This module is the single place that turns that payload into the
 * stable view-model the components render, so no component is coupled to the
 * wire shape. If the contract ever changes, only this file should change.
 *
 * Internal API job shape:
 *   { id, title, department, jobType, noOfOpenings, careerPortalUrl, publishedOn,
 *     experience, locations: [{ name, city, state, country }] }
 * The server already maps `jobType` to a label and provides the real
 * `careerPortalUrl` (empty when Keka omits it → JobCard hides the CTA, FR-6).
 *
 * View-model (what components consume): every field normalised so the UI never
 * has to defend against null/undefined:
 *   - strings default to ""
 *   - `locations` is always an array of { name, city, state, country }
 *   - `jobType` is a human label ("Full Time")
 *
 * `mapJobType` and the `countryName` fallback stay defensive so a raw upstream
 * shape (e.g. a numeric code) never leaks a digit or breaks the card.
 */
import { JOB_TYPE_MAP, DEFAULT_JOB_TYPE_LABEL } from "./jobsConstants";

/** Coerce any value to a trimmed string ("" for null/undefined/objects). */
const str = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

/**
 * jobType is a numeric code in the raw embed payload; the view-model wants a
 * label. Map known codes via JOB_TYPE_MAP; an unknown NUMERIC code falls back to
 * the default label so a raw digit is never rendered. An existing label string
 * (legacy/internal shape) passes through unchanged. Empty stays empty.
 */
const mapJobType = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const isNumericCode =
    typeof value === "number" || /^\d+$/.test(String(value).trim());
  if (isNumericCode) {
    return JOB_TYPE_MAP[Number(value)] || DEFAULT_JOB_TYPE_LABEL;
  }
  return str(value);
};

/** Normalise a single location (accepts both `country` and `countryName`). */
const adaptLocation = (loc) => ({
  name: str(loc && loc.name),
  city: str(loc && loc.city),
  state: str(loc && loc.state),
  country: str(loc && (loc.country || loc.countryName)),
});

/** Map one raw job object into the view-model. */
export const adaptJob = (raw) => {
  const job = raw || {};
  const rawLocations = Array.isArray(job.jobLocations)
    ? job.jobLocations
    : Array.isArray(job.locations)
    ? job.locations
    : [];
  const id = str(job.id);

  return {
    id,
    title: str(job.title),
    department: str(job.department || job.departmentName),
    jobType: mapJobType(job.jobType),
    locations: rawLocations.map(adaptLocation),
    experience: str(job.experience),
    noOfOpenings: str(job.noOfOpenings),
    // Real apply URL from the API; "" when absent → JobCard hides the CTA (FR-6).
    careerPortalUrl: str(job.careerPortalUrl),
    publishedOn: str(job.publishedOn),
  };
};

/**
 * Map the jobs payload into a view-model array. Accepts the internal
 * `{ jobs: [...] }` envelope (and tolerates a bare array defensively).
 * Defensive: returns [] for any unexpected shape so the UI renders the empty
 * state instead of throwing. Jobs without an `id` are dropped (they can't be
 * keyed or linked).
 */
export const adaptJobsResponse = (payload) => {
  const jobs = Array.isArray(payload)
    ? payload
    : payload && Array.isArray(payload.jobs)
    ? payload.jobs
    : [];
  return jobs.map(adaptJob).filter((job) => job.id);
};
